import { useState, useEffect, useMemo, useRef, type ChangeEvent, type ReactElement } from "react";
import {
  Loader2,
  Receipt,
  BookOpen,
  FileCheck,
  ChevronLeft,
  ChevronRight,
  ArrowLeftRight,
  MessageCircle,
  FileText,
  Upload,
  File as FileIcon,
  CheckCircle,
  AlertTriangle,
  X,
} from "lucide-react";
import { useDossierStore } from "../../store/dossierStore";
import { supabase } from "../../lib/supabase";
import { logger } from "../../lib/logger";
import {
  fetchEcritureFactureAchat,
  fetchEcritureFactureAchatCca,
  fetchEcritureQuittanceLoyerFactureAchat,
  fetchEcritureRetentionTvaFactureAchat,
  fetchEcrituresPaiementFactureAchat,
  genererEcritureFactureAchatCca,
  genererEcritureFactureAchatValidee,
  genererEcritureQuittanceLoyerFactureAchat,
  type EcritureComptable,
} from "../../lib/comptable";
import { getTypeDepenseConfig, getLabelRegimeTva, type TypeDepense } from "../../lib/type-depense";
import { labelExigibiliteTvaFactureAchat, normalizeRegimeTvaFactureAchat } from "../../lib/factureAchatTva";
import BlocEcriture, { fmtDate } from "../../components/compta/BlocEcriture";
import EcritureEditModal from "../../components/compta/EcritureEditModal";
import ComptaDrawer from "../../components/compta/ComptaDrawer";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/utils";
import { formTabButtonActiveClass, formTabButtonClass, formTabsListClass } from "@/lib/ui/formFieldClasses";
import CommentairesTab from "../../components/compta/CommentairesTab";
import { countCommentaires } from "../../lib/commentaires";
import { ensureImmobilisationDepuisFactureIfNeeded, type FactureAchatImmoMetadata } from "../../lib/immobilisations";
import { formatMontantFCFA } from "@/lib/formatMontant";
import { pieceUrlIsLikelyImage, pieceUrlIsLikelyPdf } from "@/lib/filePiecePreview";
import {
  assertEcritureModifiablePourDate,
  fetchExercices,
  findExerciceContenantDate,
  type ExerciceComptable,
} from "../../lib/exercices";
import { BUCKET_PIECES_JUSTIFICATIVES, resolveFileOrPieceUrl } from "../../lib/piecesJustificativesStorage";
import { fetchRegles, findMatchingRegleFactureAchat, type RegleCategorisation } from "../../lib/regles";
import { formatCompte8 } from "../../lib/comptable/formatCompte";
import { calculerMontantsQuittanceDepuisHt, repartirMontantQuittanceLoyerTtc } from "@/lib/fiscal/calculRSL";
import type { QuittanceLoyerMetadata } from "@/types/loyer";

const fmt = (n: number) => formatMontantFCFA(n);

/**
 * Si la fiche a montant_ht / montant_tva cohérents avec le TTC quittance mais metadata.quittance_loyer
 * est incohérent (ex. HT = TTC, pas de TVA en écriture), réaligne metadata avant régénération RPC.
 */
function digitsCompte(num: string | null | undefined): string {
  return String(num ?? "").replace(/\D/g, "");
}

/** Brouillon quittance : écriture ≠ HT/TVA attendus (souvent TTC entier en 622 sans 4452). */
function ecritureQuittanceBrouillonDesynchronisee(fact: EcritureComptable, row: FactureAchat): boolean {
  if (fact.statut !== "draft") return false;
  const ql = row.metadata?.quittance_loyer as QuittanceLoyerMetadata | undefined;
  if (!ql) return false;
  const ht = Math.round(Number(row.montant_ht) || Number(ql.montant_ht) || 0);
  const tva = Math.round(Number(row.montant_tva) || Number(ql.montant_tva) || 0);
  const brut = Math.round(Number(ql.montant_brut) || 0);
  if (brut <= 0 && ht <= 0) return false;
  const lignes = fact.lignes ?? [];
  const debit4452 = lignes.some(
    (l) => digitsCompte(l.compte_numero).startsWith("4452") && (Number(l.debit) || 0) > 0
  );
  if (tva > 0 && !debit4452) return true;
  const ligneCharge = lignes.find(
    (l) => (Number(l.debit) || 0) > 0 && !/^445/.test(digitsCompte(l.compte_numero))
  );
  const dCharge = ligneCharge ? Math.round(Number(ligneCharge.debit) || 0) : 0;
  const ttc = ht + tva > 0 ? ht + tva : brut;
  if (ht > 0 && tva > 0 && Math.abs(dCharge - ttc) <= 1 && Math.abs(dCharge - ht) > 1) return true;
  return false;
}

async function alignerMetadataQuittanceDepuisColonnesFacture(
  factureId: string,
  meta: FactureAchat["metadata"] | undefined | null,
  montantHtCol: number,
  montantTvaCol: number
): Promise<FactureAchat["metadata"] | null> {
  const ql = meta?.quittance_loyer as QuittanceLoyerMetadata | undefined;
  if (!ql?.type_bailleur) return null;

  const brutQl = Math.round(Number(ql.montant_brut) || 0);
  const netRsl = Math.round((Number(ql.montant_net) || 0) + (Number(ql.montant_rsl) || 0));
  const ttcRef = brutQl > 0 ? brutQl : netRsl;
  if (ttcRef <= 0) return null;

  let htEff = Math.round(Number(montantHtCol) || 0);
  let tvaEff = Math.round(Number(montantTvaCol) || 0);

  if (htEff + tvaEff > 0 && Math.abs(htEff + tvaEff - ttcRef) <= 1) {
    /* colonnes facture cohérentes avec le TTC */
  } else {
    htEff = 0;
    tvaEff = 0;
    const qh = Math.round(Number(ql.montant_ht) || 0);
    const qt = Math.round(Number(ql.montant_tva) || 0);
    const htTraiteCommeTtc = qh > 0 && Math.abs(qh - ttcRef) <= 1 && qt <= 0;
    if (qh > 0 && qt >= 0 && Math.abs(qh + qt - ttcRef) <= 1 && !htTraiteCommeTtc) {
      htEff = qh;
      tvaEff = qt;
    } else if (brutQl > 0) {
      const rep = repartirMontantQuittanceLoyerTtc(brutQl);
      htEff = rep.montant_ht;
      tvaEff = rep.montant_tva;
    }
  }

  if (htEff <= 0 && tvaEff <= 0) return null;
  if (Math.abs(htEff + tvaEff - ttcRef) > 1) return null;

  const tauxTva: 0 | 18 = tvaEff > 0 ? 18 : 0;
  const r = calculerMontantsQuittanceDepuisHt(htEff, tauxTva, ql.type_bailleur);

  const qlHt = Math.round(Number(ql.montant_ht) || 0);
  const qlTva = Math.round(Number(ql.montant_tva) || 0);
  if (
    qlHt === r.montantHt &&
    qlTva === r.montantTva &&
    Math.abs((Number(ql.montant_rsl) || 0) - r.montantRSL) <= 1 &&
    Math.abs((Number(ql.montant_net) || 0) - r.montantNet) <= 1
  ) {
    return null;
  }

  const nextQl: QuittanceLoyerMetadata = {
    ...ql,
    montant_ht: r.montantHt,
    montant_tva: r.montantTva,
    montant_brut: r.montantBrut,
    taux_tva_pct: r.tauxTvaPct,
    montant_rsl: r.montantRSL,
    montant_net: r.montantNet,
    taux_rsl_pct: r.tauxRSL,
    montant_saisi: r.montantHt,
    est_montant_net: false,
  };
  const nextMeta: FactureAchat["metadata"] = {
    ...(meta ?? {}),
    quittance_loyer: nextQl as unknown,
  };
  const { error } = await supabase
    .from("factures_achat")
    .update({
      metadata: nextMeta,
      montant_ht: r.montantHt,
      montant_tva: r.montantTva,
      montant_ttc: r.montantNet,
    })
    .eq("id", factureId);
  if (error) {
    logger.warn("[COMPTA] alignement metadata quittance:", error.message);
    return null;
  }
  return nextMeta;
}

type AchatStatut = "boite_reception" | "a_payer" | "partiel" | "payee" | "en_retard" | "archive";

type ComptaFilterTab = "toutes" | "boite_reception" | "pre_traitee" | "payee";

const COMPTA_BADGE_A_TRAITER = { bg: "#eff6ff", color: "#2563eb", border: "#dbeafe", label: "À traiter" };
const COMPTA_BADGE_PRE_TRAITEE = { bg: "#fff7ed", color: "#ea580c", border: "#fed7aa", label: "Pré-traitée" };
const COMPTA_BADGE_TRAITEE = { bg: "#ecfdf5", color: "#059669", border: "#d1fae5", label: "Traitée" };
const COMPTA_BADGE_ARCHIVE = { bg: "#f1f5f9", color: "#475569", border: "#e2e8f0", label: "Archivée" };

function comptaBadgeForStatut(statut: AchatStatut): { bg: string; color: string; border: string; label: string } {
  if (statut === "boite_reception") return COMPTA_BADGE_A_TRAITER;
  if (statut === "a_payer" || statut === "en_retard") return COMPTA_BADGE_PRE_TRAITEE;
  if (statut === "payee") return COMPTA_BADGE_TRAITEE;
  return COMPTA_BADGE_ARCHIVE;
}

const FILTER_TABS: { id: ComptaFilterTab; label: string }[] = [
  { id: "toutes", label: "Toutes" },
  { id: "boite_reception", label: "À traiter" },
  { id: "pre_traitee", label: "Pré-traitée" },
  { id: "payee", label: "Traitée" },
];

interface FactureAchat {
  id: string;
  statut: AchatStatut;
  statut_rapprochement?: string;
  date_emission: string;
  fournisseur_nom: string;
  numero: string;
  montant_ht: number;
  montant_tva: number;
  montant_ttc: number;
  type_document?: string;
  fournisseur_fk?: string | null;
  /** Exigibilité TVA déductible (colonne SQL), distincte de metadata.regime_tva (biens/services). */
  regime_tva?: string | null;
  file_url?: string | null;
  metadata?: {
    type_depense?: string;
    regime_tva?: string;
    taux_tva?: number;
    is_immobilisation?: boolean;
    quittance_loyer?: unknown;
    lignes_ht_tva?: { ht: number; tva: number }[];
    /** Saisie compta / règle centre : priorité dans `generer_facture_achat_validee` */
    compte_charge_numero?: string;
    compta_compte_charge_source_regle_fournisseur?: boolean;
  };
  cca_detecte?: boolean;
  cca_genere?: boolean;
  cca_montant?: number;
  cca_prorata?: number;
}

/** Affichage court : 62220000 → 6222 */
function compteNumeroAfficheListe(raw: string): string {
  const d = (raw ?? "").replace(/\D/g, "");
  if (!d) return "—";
  const t = d.replace(/0+$/, "");
  return t.length > 0 ? t : d;
}

/**
 * Ordre : écriture générée → compte charge fiche fournisseur → quittance / immo / type dépense → 658 (défaut SQL).
 */
function compteChargeListeFactureAchat(
  f: FactureAchat,
  compteDepuisEcriture?: string | null,
  compteDepuisFournisseur?: string | null
): string {
  const e = compteDepuisEcriture?.trim();
  if (e) return compteNumeroAfficheListe(e);
  const cf = compteDepuisFournisseur?.trim();
  if (cf) return compteNumeroAfficheListe(cf);
  if (f.type_document === "quittance_loyer") return "6222";
  if (f.metadata?.is_immobilisation === true) return "2xxx";
  const td = f.metadata?.type_depense;
  if (td) {
    const cfg = getTypeDepenseConfig(td as TypeDepense);
    if (cfg?.compte_propose) return cfg.compte_propose;
  }
  return "658";
}

/** Charge le 6… de chaque écriture facture_achat_validee / quittance (requête groupée). */
async function fetchComptesChargeListeFactures(factureIds: string[]): Promise<Record<string, string>> {
  const out: Record<string, string> = {};
  if (!factureIds.length) return out;
  const isTva = (num: string) => /^445/.test((num || "").replace(/\D/g, ""));
  const chunkSize = 80;
  for (let i = 0; i < factureIds.length; i += chunkSize) {
    const chunk = factureIds.slice(i, i + chunkSize);
    const { data: ecritures, error: eErr } = await supabase
      .from("ecritures_comptables")
      .select("id, source_id")
      .eq("source_type", "facture_achat")
      .in("source_id", chunk)
      .in("event_type", ["facture_achat_validee", "quittance_loyer_facture_achat"]);
    if (eErr || !ecritures?.length) continue;
    const ecritureIds = ecritures.map((e) => e.id);
    const { data: lignes, error: lErr } = await supabase
      .from("ecritures_comptables_lignes")
      .select("ecriture_id, compte_numero, debit, created_at")
      .in("ecriture_id", ecritureIds)
      .order("created_at", { ascending: true });
    if (lErr || !lignes?.length) continue;
    const byEcriture = new Map<string, typeof lignes>();
    for (const l of lignes) {
      const arr = byEcriture.get(l.ecriture_id) ?? [];
      arr.push(l);
      byEcriture.set(l.ecriture_id, arr);
    }
    for (const ec of ecritures) {
      const ls = byEcriture.get(ec.id) ?? [];
      const charge = ls.find((l) => (Number(l.debit) || 0) > 0 && !isTva(String(l.compte_numero ?? "")));
      if (charge?.compte_numero) {
        out[ec.source_id] = String(charge.compte_numero).replace(/\s/g, "");
      }
    }
  }
  return out;
}

type PillStyle = { background: string; color: string; border: string };

function pillCompteCharge(label: string): PillStyle {
  if (!label || label === "—") {
    return { background: "#f1f5f9", color: "#64748b", border: "#e2e8f0" };
  }
  const s = label.replace(/\s/g, "").toLowerCase();
  if (s.includes("2xxx") || /^2\d{2}/.test(s)) {
    return { background: "#d1fae5", color: "#047857", border: "#6ee7b7" };
  }
  if (s.startsWith("44") || s.includes("44x")) {
    return { background: "#f1f5f9", color: "#334155", border: "#cbd5e1" };
  }
  const digits = s.replace(/x/gi, "0").replace(/[^\d]/g, "");
  const p2 = digits.length >= 2 ? parseInt(digits.slice(0, 2), 10) : NaN;
  if (!Number.isNaN(p2)) {
    if (p2 >= 60 && p2 < 61) return { background: "#dbeafe", color: "#1d4ed8", border: "#93c5fd" };
    if (p2 === 61) return { background: "#e0e7ff", color: "#4338ca", border: "#a5b4fc" };
    if (p2 === 62) return { background: "#ede9fe", color: "#6d28d9", border: "#c4b5fd" };
    if (p2 === 63) return { background: "#fef3c7", color: "#b45309", border: "#fcd34d" };
    if (p2 === 64 || p2 === 65 || p2 === 66) return { background: "#fce7f3", color: "#be185d", border: "#f9a8d4" };
  }
  if (s.startsWith("60") || s.includes("60xx")) {
    return { background: "#dbeafe", color: "#1e40af", border: "#93c5fd" };
  }
  if (s.startsWith("62") || s.includes("62xx")) {
    return { background: "#ede9fe", color: "#5b21b6", border: "#c4b5fd" };
  }
  return { background: "#f1f5f9", color: "#334155", border: "#e2e8f0" };
}

function tauxTvaPillFactureAchat(f: FactureAchat): { label: string; style: PillStyle } {
  const neutral: PillStyle = { background: "#f1f5f9", color: "#64748b", border: "#e2e8f0" };
  const regime = f.metadata?.regime_tva;
  if (regime === "non_applicable") {
    return { label: "Aucune", style: neutral };
  }

  const lignes = f.metadata?.lignes_ht_tva;
  if (Array.isArray(lignes) && lignes.length >= 2) {
    const rates = lignes
      .map((l) => {
        const ht = Number(l.ht) || 0;
        const tva = Number(l.tva) || 0;
        if (ht <= 0) return null;
        return Math.round((tva / ht) * 10000) / 100;
      })
      .filter((x): x is number => x !== null);
    const uniq = [...new Set(rates)];
    if (uniq.length > 1) {
      return {
        label: "Multitaux",
        style: { background: "#f3e8ff", color: "#6b21a8", border: "#d8b4fe" },
      };
    }
  }

  const tMeta = f.metadata?.taux_tva;
  let pct: number | null = null;
  if (typeof tMeta === "number" && !Number.isNaN(tMeta)) {
    pct = Math.round(tMeta * 100) / 100;
  } else {
    const ht = Number(f.montant_ht) || 0;
    const tva = Number(f.montant_tva) || 0;
    if (ht > 0) pct = Math.round((tva / ht) * 10000) / 100;
    else if (tva === 0) pct = 0;
  }

  if (pct === null) {
    return { label: "—", style: { background: "#f8fafc", color: "#94a3b8", border: "#e2e8f0" } };
  }
  if (pct === 0) {
    return { label: "Aucune", style: neutral };
  }

  const labelNum = Number.isInteger(pct) ? String(pct) : String(Math.round(pct * 100) / 100);
  const label = `${labelNum} %`;

  if (Math.abs(pct - 20) < 0.05) {
    return {
      label,
      style: { background: "#dcfce7", color: "#166534", border: "#86efac" },
    };
  }
  if ([18, 10, 5.5, 8.5, 2.1].some((t) => Math.abs(pct - t) < 0.05)) {
    return {
      label,
      style: { background: "#ffedd5", color: "#c2410c", border: "#fdba74" },
    };
  }

  return {
    label,
    style: { background: "#e0f2fe", color: "#0369a1", border: "#7dd3fc" },
  };
}

function ListeFacturePill(props: { text: string; style: PillStyle; mono?: boolean }): ReactElement {
  const { text, style, mono } = props;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 12,
        fontWeight: 600,
        padding: "5px 11px",
        borderRadius: 9999,
        background: style.background,
        color: style.color,
        border: `1px solid ${style.border}`,
        whiteSpace: "nowrap",
        fontVariantNumeric: "tabular-nums",
        fontFamily: mono
          ? 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace'
          : undefined,
        lineHeight: 1.2,
      }}
    >
      {text}
    </span>
  );
}

/** Première ligne HT (hors TVA 445x) pour prévisualiser le crédit CCA. */
function premiereLigneChargeHt(ecriture: EcritureComptable | null | "loading"): { compte: string; libelle: string } | null {
  if (!ecriture || ecriture === "loading" || !ecriture.lignes?.length) return null;
  const isTva = (num: string) => /^445/.test((num || "").replace(/\D/g, ""));
  const ligne = ecriture.lignes.find((l) => l.debit > 0 && !isTva(l.compte_numero));
  if (!ligne) return null;
  return { compte: ligne.compte_numero, libelle: ligne.compte_libelle };
}

function matchesSearch(f: FactureAchat, q: string): boolean {
  if (!q.trim()) return true;
  const s = q.trim().toLowerCase();
  return (f.fournisseur_nom || "").toLowerCase().includes(s) || (f.numero || "").toLowerCase().includes(s);
}

function matchesStatutFilter(f: FactureAchat, tab: ComptaFilterTab): boolean {
  if (tab === "toutes") return true;
  if (tab === "pre_traitee") return f.statut === "a_payer" || f.statut === "en_retard";
  return f.statut === tab;
}

const PAGE_SIZE = 25;

function safeStorageFileName(file: File): string {
  const raw = file.name.replace(/[/\\]/g, "_");
  const cleaned = raw.replace(/[^a-zA-Z0-9._-]/g, "_") || "piece";
  return `${Date.now()}_${cleaned}`;
}

/** Nom affiché : dernier segment de chemin, décodé, préfixe horodatage `1234567890_` retiré. */
function displayFileNameFromUrl(url: string): string {
  const pathOnly = url.split(/[?#]/)[0];
  const rawSeg = pathOnly.split("/").filter(Boolean).pop() || "";
  let decoded = rawSeg;
  try {
    decoded = decodeURIComponent(rawSeg);
  } catch {
    decoded = rawSeg;
  }
  const withoutTs = decoded.replace(/^\d{10,}_(.+)$/, "$1");
  return withoutTs.trim() || decoded.trim() || "Fichier";
}

export default function FacturesAchatCompta() {
  const { activeDossier } = useDossierStore();
  const [loading, setLoading] = useState(true);
  const [factures, setFactures] = useState<FactureAchat[]>([]);
  const [selectedFacture, setSelectedFacture] = useState<FactureAchat | null>(null);
  const [filterStatut, setFilterStatut] = useState<ComptaFilterTab>("toutes");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [ecritureFacture, setEcritureFacture] = useState<EcritureComptable | null | "loading">(null);
  const [ecritureCCA, setEcritureCCA] = useState<EcritureComptable | null | "loading">(null);
  const [generatingCca, setGeneratingCca] = useState(false);
  const [exercicesDossier, setExercicesDossier] = useState<ExerciceComptable[]>([]);
  const [ecritureRetentionTva, setEcritureRetentionTva] = useState<EcritureComptable | null>(null);
  const [drawerTab, setDrawerTab] = useState<"ecritures" | "reconciliation" | "commentaires">("ecritures");
  const [uploadingFacturePiece, setUploadingFacturePiece] = useState(false);
  const facturePieceInputRef = useRef<HTMLInputElement>(null);
  const [editingEcriture, setEditingEcriture] = useState<EcritureComptable | null>(null);
  const [ecrituresRefreshKey, setEcrituresRefreshKey] = useState(0);
  const [commentairesCount, setCommentairesCount] = useState(0);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [validating, setValidating] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [validatedCount, setValidatedCount] = useState(0);
  const [resolvedFactureFileUrl, setResolvedFactureFileUrl] = useState<string | null>(null);
  /** Compte 6xx issu de l’écriture (facture_achat_validee / quittance), clé = facture id */
  const [chargesParFactureId, setChargesParFactureId] = useState<Record<string, string>>({});
  /** Compte de charge renseigné sur la fiche fournisseur */
  const [compteChargeParFournisseurFk, setCompteChargeParFournisseurFk] = useState<Record<string, string>>({});
  /** Règles « Factures achat » (fournisseur → compte de charge) pour appliquer le compte sur l’écriture brouillon */
  const [reglesFournisseur, setReglesFournisseur] = useState<RegleCategorisation[]>([]);

  useEffect(() => {
    if (!activeDossier?.id) {
      setReglesFournisseur([]);
      return;
    }
    let cancelled = false;
    void fetchRegles(activeDossier.id)
      .then((rows) => {
        if (!cancelled) setReglesFournisseur(rows);
      })
      .catch(() => {
        if (!cancelled) setReglesFournisseur([]);
      });
    return () => {
      cancelled = true;
    };
  }, [activeDossier?.id]);

  useEffect(() => {
    if (!factures.length) {
      setChargesParFactureId({});
      setCompteChargeParFournisseurFk({});
      return;
    }
    let cancelled = false;
    void (async () => {
      const ids = [...new Set(factures.map((f) => f.id))];
      const fks = [
        ...new Set(factures.map((f) => f.fournisseur_fk).filter((x): x is string => Boolean(x))),
      ];
      const fkMap: Record<string, string> = {};
      if (fks.length > 0) {
        const { data: fRows, error: fkErr } = await supabase
          .from("fournisseurs")
          .select("id, compte_charge_numero")
          .in("id", fks);
        if (!fkErr && fRows) {
          for (const row of fRows) {
            const n = row.compte_charge_numero != null ? String(row.compte_charge_numero).trim() : "";
            if (n) fkMap[row.id] = n.replace(/\s/g, "");
          }
        }
      }
      const chargeMap = await fetchComptesChargeListeFactures(ids);
      if (!cancelled) {
        setChargesParFactureId(chargeMap);
        setCompteChargeParFournisseurFk(fkMap);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [factures, ecrituresRefreshKey]);

  useEffect(() => {
    if (!activeDossier?.id) return;
    setLoading(true);
    (async () => {
      try {
        const { data, error } = await supabase
          .from("factures_achat")
          .select(
            "id, statut, statut_rapprochement, date_emission, fournisseur_nom, fournisseur_fk, regime_tva, numero, montant_ht, montant_tva, montant_ttc, type_document, file_url, metadata, cca_detecte, cca_genere, cca_montant, cca_prorata"
          )
          .eq("dossier_id", activeDossier.id)
          .order("created_at", { ascending: false });
        if (error) throw error;
        setFactures((data as FactureAchat[]) || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [activeDossier?.id]);

  useEffect(() => {
    if (!activeDossier?.id) {
      setExercicesDossier([]);
      return;
    }
    void fetchExercices(activeDossier.id)
      .then(setExercicesDossier)
      .catch(() => setExercicesDossier([]));
  }, [activeDossier?.id]);

  const exercicePourFacture = useMemo(() => {
    if (!selectedFacture?.date_emission) return null;
    return findExerciceContenantDate(exercicesDossier, selectedFacture.date_emission);
  }, [exercicesDossier, selectedFacture?.date_emission]);

  useEffect(() => {
    if (!selectedFacture?.id || !selectedFacture.cca_detecte) {
      setEcritureCCA(null);
      return;
    }
    let cancelled = false;
    setEcritureCCA("loading");
    void fetchEcritureFactureAchatCca(selectedFacture.id).then((ec) => {
      if (!cancelled) setEcritureCCA(ec);
    });
    return () => {
      cancelled = true;
    };
  }, [selectedFacture?.id, selectedFacture?.cca_detecte, ecrituresRefreshKey]);

  useEffect(() => {
    if (!selectedFacture?.id) {
      setEcritureFacture(null);
      setEcritureRetentionTva(null);
      return;
    }
    let cancelled = false;
    setEcritureFacture("loading");
    setEcritureRetentionTva(null);
    (async () => {
      /* Quittances : `type_document = 'quittance_loyer'` (contrainte factures_achat_type_document_check). */
      const isQuittance = selectedFacture!.type_document === "quittance_loyer";
      let fact: EcritureComptable | null = null;
      let retention: EcritureComptable | null = null;
      try {
        if (isQuittance) {
          fact = await fetchEcritureQuittanceLoyerFactureAchat(selectedFacture!.id);
          retention = null;
        } else {
          [fact, retention] = await Promise.all([
            fetchEcritureFactureAchat(selectedFacture!.id),
            fetchEcritureRetentionTvaFactureAchat(selectedFacture!.id),
          ]);
        }
      } catch (err) {
        console.error("[COMPTA] Erreur fetch écritures:", err);
      }

      /* Écriture brouillon déjà présente mais obsolète (ex. TTC en 622 sans 4452) : réaligner + régénérer. */
      if (
        isQuittance &&
        fact &&
        fact.statut === "draft" &&
        ecritureQuittanceBrouillonDesynchronisee(fact, selectedFacture!)
      ) {
        try {
          const alignedResync = await alignerMetadataQuittanceDepuisColonnesFacture(
            selectedFacture!.id,
            selectedFacture!.metadata,
            selectedFacture!.montant_ht,
            selectedFacture!.montant_tva
          );
          if (alignedResync && !cancelled) {
            const qlu = alignedResync.quittance_loyer as QuittanceLoyerMetadata | undefined;
            const montPatch: Partial<FactureAchat> = {};
            if (qlu && typeof qlu.montant_ht === "number") {
              montPatch.montant_ht = qlu.montant_ht;
              montPatch.montant_tva = qlu.montant_tva ?? 0;
              if (typeof qlu.montant_net === "number") montPatch.montant_ttc = qlu.montant_net;
            }
            setSelectedFacture((prev) =>
              prev?.id === selectedFacture!.id
                ? { ...prev, metadata: alignedResync, ...montPatch }
                : prev
            );
            setFactures((prev) =>
              prev.map((row) =>
                row.id === selectedFacture!.id ? { ...row, metadata: alignedResync, ...montPatch } : row
              )
            );
          }
          await genererEcritureQuittanceLoyerFactureAchat(selectedFacture!.id);
          fact = await fetchEcritureQuittanceLoyerFactureAchat(selectedFacture!.id);
          retention = null;
        } catch (e) {
          logger.warn("[COMPTA] resync écriture quittance brouillon:", e);
        }
      }

      if (!fact && selectedFacture!.statut && ["a_payer", "payee", "partiel", "en_retard"].includes(selectedFacture!.statut)) {
        try {
          if (isQuittance) {
            const aligned = await alignerMetadataQuittanceDepuisColonnesFacture(
              selectedFacture!.id,
              selectedFacture!.metadata,
              selectedFacture!.montant_ht,
              selectedFacture!.montant_tva
            );
            if (aligned && !cancelled) {
              const qlu = aligned.quittance_loyer as QuittanceLoyerMetadata | undefined;
              const montPatch: Partial<FactureAchat> = {};
              if (qlu && typeof qlu.montant_ht === "number") {
                montPatch.montant_ht = qlu.montant_ht;
                montPatch.montant_tva = qlu.montant_tva ?? 0;
                if (typeof qlu.montant_net === "number") montPatch.montant_ttc = qlu.montant_net;
              }
              setSelectedFacture((prev) =>
                prev?.id === selectedFacture!.id ? { ...prev, metadata: aligned, ...montPatch } : prev
              );
              setFactures((prev) =>
                prev.map((row) =>
                  row.id === selectedFacture!.id ? { ...row, metadata: aligned, ...montPatch } : row
                )
              );
            }
            await genererEcritureQuittanceLoyerFactureAchat(selectedFacture!.id);
            fact = await fetchEcritureQuittanceLoyerFactureAchat(selectedFacture!.id);
            retention = null;
          } else {
            try {
              await ensureImmobilisationDepuisFactureIfNeeded(
                selectedFacture!.id,
                selectedFacture!.metadata as FactureAchatImmoMetadata | null | undefined
              );
            } catch (immoErr) {
              console.error("[COMPTA] Création immobilisation facture:", immoErr);
            }
            await genererEcritureFactureAchatValidee(selectedFacture!.id);
            [fact, retention] = await Promise.all([
              fetchEcritureFactureAchat(selectedFacture!.id),
              fetchEcritureRetentionTvaFactureAchat(selectedFacture!.id),
            ]);
          }
        } catch (err) {
          console.error("[COMPTA] Génération différée échouée:", err);
        }
      }

      if (
        fact &&
        fact.statut === "draft" &&
        reglesFournisseur.length > 0 &&
        (selectedFacture!.fournisseur_nom ?? "").trim()
      ) {
        const regleMatch = findMatchingRegleFactureAchat(
          { fournisseur_nom: selectedFacture!.fournisseur_nom },
          reglesFournisseur
        );
        const ruleRaw = regleMatch?.compte_charge_numero?.trim();
        if (ruleRaw) {
          const rule8 = formatCompte8(ruleRaw);
          const isTvaLigne = (num: string | null | undefined) =>
            /^445/.test(String(num ?? "").replace(/\D/g, ""));
          const chargeLigne = fact.lignes?.find(
            (l) => (Number(l.debit) || 0) > 0 && !isTvaLigne(l.compte_numero)
          );
          const cur8 = chargeLigne ? formatCompte8(String(chargeLigne.compte_numero ?? "")) : "";
          if (chargeLigne && cur8 !== rule8) {
            try {
              const prevMeta = (selectedFacture!.metadata ?? {}) as FactureAchat["metadata"];
              const newMeta: FactureAchat["metadata"] = {
                ...prevMeta,
                compte_charge_numero: rule8,
                compta_compte_charge_source_regle_fournisseur: true,
              };
              const { error: upMetaErr } = await supabase
                .from("factures_achat")
                .update({ metadata: newMeta })
                .eq("id", selectedFacture!.id);
              if (upMetaErr) throw upMetaErr;
              let metaAffiche: FactureAchat["metadata"] = newMeta;
              if (isQuittance) {
                const aligned = await alignerMetadataQuittanceDepuisColonnesFacture(
                  selectedFacture!.id,
                  newMeta,
                  selectedFacture!.montant_ht,
                  selectedFacture!.montant_tva
                );
                if (aligned) metaAffiche = aligned;
              }
              if (isQuittance) {
                await genererEcritureQuittanceLoyerFactureAchat(selectedFacture!.id);
                if (!cancelled) {
                  fact = await fetchEcritureQuittanceLoyerFactureAchat(selectedFacture!.id);
                  retention = null;
                }
              } else {
                await genererEcritureFactureAchatValidee(selectedFacture!.id);
                if (!cancelled) {
                  [fact, retention] = await Promise.all([
                    fetchEcritureFactureAchat(selectedFacture!.id),
                    fetchEcritureRetentionTvaFactureAchat(selectedFacture!.id),
                  ]);
                }
              }
              if (!cancelled) {
              const qlu = metaAffiche.quittance_loyer as QuittanceLoyerMetadata | undefined;
              const montPatch: Partial<FactureAchat> = {};
              if (isQuittance && qlu && typeof qlu.montant_ht === "number") {
                montPatch.montant_ht = qlu.montant_ht;
                montPatch.montant_tva = qlu.montant_tva ?? 0;
                if (typeof qlu.montant_net === "number") montPatch.montant_ttc = qlu.montant_net;
              }
              setFactures((prev) =>
                prev.map((row) =>
                  row.id === selectedFacture!.id ? { ...row, metadata: metaAffiche, ...montPatch } : row
                )
              );
              setSelectedFacture((prev) =>
                prev?.id === selectedFacture!.id
                  ? { ...prev, metadata: metaAffiche, ...montPatch }
                  : prev
              );
              }
            } catch (metaErr) {
              console.error("[COMPTA] Application règle fournisseur (compte charge) :", metaErr);
            }
          }
        }
      }

      if (!cancelled) {
        setEcritureFacture(fact);
        setEcritureRetentionTva(retention);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    selectedFacture?.id,
    selectedFacture?.statut,
    selectedFacture?.type_document,
    selectedFacture?.fournisseur_nom,
    ecrituresRefreshKey,
    reglesFournisseur,
  ]);

  const filtered = useMemo(
    () => factures.filter((f) => matchesStatutFilter(f, filterStatut) && matchesSearch(f, search)),
    [factures, filterStatut, search]
  );

  const tabCounts = useMemo(() => {
    const q = search;
    return {
      toutes: factures.filter((f) => matchesSearch(f, q)).length,
      boite_reception: factures.filter((f) => matchesSearch(f, q) && f.statut === "boite_reception").length,
      pre_traitee: factures.filter((f) => matchesSearch(f, q) && (f.statut === "a_payer" || f.statut === "en_retard")).length,
      payee: factures.filter((f) => matchesSearch(f, q) && f.statut === "payee").length,
    };
  }, [factures, search]);

  const totalCount = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const paginatedFactures = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, safePage]);
  const startIdx = totalCount === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1;
  const endIdx = Math.min(safePage * PAGE_SIZE, totalCount);

  useEffect(() => {
    setPage(1);
  }, [filterStatut, search]);

  useEffect(() => {
    if (!selectedFacture?.id) return;
    const fresh = factures.find((x) => x.id === selectedFacture.id);
    if (fresh) setSelectedFacture(fresh);
  }, [factures, selectedFacture?.id]);

  useEffect(() => {
    setSelectedIds((prev) => {
      const next = new Set<string>();
      const validIds = new Set(factures.map((f) => f.id));
      prev.forEach((id) => {
        if (validIds.has(id)) next.add(id);
      });
      return next;
    });
  }, [factures]);

  useEffect(() => {
    if (!selectedFacture?.id) {
      setCommentairesCount(0);
      return;
    }
    let cancelled = false;
    countCommentaires("facture_achat", selectedFacture.id)
      .then((n) => {
        if (!cancelled) setCommentairesCount(n);
      })
      .catch(() => {
        if (!cancelled) setCommentairesCount(0);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedFacture?.id]);

  useEffect(() => {
    const raw = selectedFacture?.file_url?.trim();
    if (!raw) {
      setResolvedFactureFileUrl(null);
      return;
    }
    let cancelled = false;
    void resolveFileOrPieceUrl(raw).then((u) => {
      if (!cancelled) setResolvedFactureFileUrl(u);
    });
    return () => {
      cancelled = true;
    };
  }, [selectedFacture?.file_url]);

  const handleGenererEcritureCca = async () => {
    if (!selectedFacture?.id || !activeDossier?.id) return;
    const ex = exercicePourFacture;
    if (!ex) {
      window.alert("Exercice comptable introuvable pour la date de cette facture.");
      return;
    }
    const dateCloture = ex.date_fin.slice(0, 10);
    setGeneratingCca(true);
    try {
      await assertEcritureModifiablePourDate(activeDossier.id, dateCloture);
      await genererEcritureFactureAchatCca(selectedFacture.id);
      const { data: row, error: selErr } = await supabase
        .from("factures_achat")
        .select("cca_genere")
        .eq("id", selectedFacture.id)
        .maybeSingle();
      if (selErr) throw selErr;
      const cca_genere = row?.cca_genere === true;
      setFactures((prev) => prev.map((f) => (f.id === selectedFacture.id ? { ...f, cca_genere } : f)));
      setSelectedFacture((prev) => (prev?.id === selectedFacture.id ? { ...prev, cca_genere } : prev));
      setEcrituresRefreshKey((k) => k + 1);
    } catch (e) {
      console.error("[COMPTA] Génération CCA:", e);
      window.alert(e instanceof Error ? e.message : "Impossible de générer l'écriture CCA.");
    } finally {
      setGeneratingCca(false);
    }
  };

  const handleFacturePieceFileSelected = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !selectedFacture?.id || !activeDossier?.id) return;

    const path = `${activeDossier.id}/factures-achat-compta/${selectedFacture.id}/${safeStorageFileName(file)}`;
    const contentType = file.type || "application/octet-stream";

    setUploadingFacturePiece(true);
    try {
      const { error: upErr } = await supabase.storage.from(BUCKET_PIECES_JUSTIFICATIVES).upload(path, file, {
        upsert: true,
        contentType,
      });
      if (upErr) throw upErr;

      const { error: dbErr } = await supabase.from("factures_achat").update({ file_url: path }).eq("id", selectedFacture.id);
      if (dbErr) throw dbErr;

      setFactures((prev) => prev.map((f) => (f.id === selectedFacture.id ? { ...f, file_url: path } : f)));
      setSelectedFacture((prev) => (prev?.id === selectedFacture.id ? { ...prev, file_url: path } : prev));
    } catch (err) {
      console.error("[COMPTA] Upload pièce facture achat:", err);
    } finally {
      setUploadingFacturePiece(false);
    }
  };

  const handleValidateSelected = async () => {
    if (selectedIds.size === 0) return;
    setValidating(true);
    try {
      const { data, error } = await supabase
        .from("ecritures_comptables")
        .update({ statut: "validated" })
        .eq("source_type", "facture_achat")
        .in("source_id", Array.from(selectedIds))
        .eq("statut", "draft")
        .select();
      if (error) throw error;
      setValidatedCount(data?.length ?? selectedIds.size);
      setSelectedIds(new Set());
      setEcrituresRefreshKey((k) => k + 1);
      setShowSuccessModal(true);
    } catch (err) {
      logger.error("[COMPTA] Validation en masse:", err);
      window.alert("Erreur lors de la validation");
    } finally {
      setValidating(false);
    }
  };

  const typeDepense = selectedFacture?.metadata?.type_depense;
  const regimeTvaTypeDepense = selectedFacture?.metadata?.regime_tva;
  const exigibiliteTvaFacture = selectedFacture
    ? normalizeRegimeTvaFactureAchat(selectedFacture.regime_tva)
    : "debits";
  const configType = typeDepense ? getTypeDepenseConfig(typeDepense as never) : undefined;
  const statutComptable = ecritureFacture === "loading" ? "Chargement…" : ecritureFacture ? (ecritureFacture.statut === "validated" ? "Validé" : "Brouillon") : "Non généré";
  const badgeMetier = selectedFacture ? comptaBadgeForStatut(selectedFacture.statut) : null;

  if (!activeDossier) {
    return (
      <div style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif", padding: 24 }}>
        <div style={{ fontWeight: 800, marginBottom: 8, color: "#1A0A00" }}>Sélectionnez un dossier</div>
        <div style={{ fontSize: "0.9rem", color: "#9A8878" }}>Choisissez un dossier pour consulter les écritures comptables.</div>
      </div>
    );
  }

  return (
    <div
      style={{
        fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 64px - 56px)",
        background: "#f8fafc",
        overflow: "hidden",
      }}
    >
      <div style={{ padding: "20px 24px", borderBottom: "1px solid #e2e8f0", background: "white" }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: "#0f172a", margin: "0 0 8px 0" }}>Factures fournisseurs</h1>
        <p style={{ fontSize: 14, color: "#475569", margin: "0 0 16px 0" }}>Vue comptable — cliquez sur une facture pour afficher les détails</p>

        <div style={{ display: "flex", borderBottom: "1px solid #e5e7eb", marginBottom: 12, alignItems: "center" }}>
          {FILTER_TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setFilterStatut(t.id)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 16px",
                marginBottom: -1,
                border: "none",
                borderBottom: filterStatut === t.id ? "2px solid #f97316" : "2px solid transparent",
                background: "transparent",
                color: filterStatut === t.id ? "#f97316" : "#6b7280",
                fontSize: 14,
                fontWeight: filterStatut === t.id ? 600 : 500,
                cursor: "pointer",
                transition: "color 0.15s, border-color 0.15s",
              }}
            >
              {t.label}
              <span
                style={{
                  marginLeft: 6,
                  fontSize: 11,
                  fontWeight: 700,
                  padding: "2px 6px",
                  borderRadius: 999,
                  background: filterStatut === t.id ? "#fff7ed" : "#f3f4f6",
                  color: filterStatut === t.id ? "#f97316" : "#6b7280",
                }}
              >
                {tabCounts[t.id]}
              </span>
            </button>
          ))}
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
          <input
            type="text"
            placeholder="Rechercher fournisseur, n°…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 280, maxWidth: "100%", padding: "8px 14px", borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 13, outline: "none", fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}
          />
        </div>
      </div>

      <div style={{ flex: 1, overflow: "auto" }}>
        {loading ? (
          <div style={{ padding: 48, textAlign: "center", color: "#475569" }}>
            <Loader2 size={32} className="animate-spin" style={{ animation: "spin 1s linear infinite" }} />
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", background: "white" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid #f3f4f6", background: "#fafafa" }}>
                <th style={{ padding: "6px 10px", textAlign: "center", width: 36, verticalAlign: "middle" }}>
                  <input
                    type="checkbox"
                    checked={filtered.length > 0 && filtered.every((f) => selectedIds.has(f.id))}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedIds(new Set(filtered.map((f) => f.id)));
                      } else {
                        setSelectedIds((prev) => {
                          const next = new Set(prev);
                          filtered.forEach((f) => next.delete(f.id));
                          return next;
                        });
                      }
                    }}
                    style={{ width: 14, height: 14, accentColor: "#f97316", cursor: "pointer", verticalAlign: "middle" }}
                  />
                </th>
                <th style={{ padding: "6px 12px", textAlign: "center", fontSize: 11, fontWeight: 500, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.04em" }}>Statut</th>
                <th style={{ padding: "6px 12px", textAlign: "left", fontSize: 11, fontWeight: 500, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.04em" }}>Date</th>
                <th style={{ padding: "6px 12px", textAlign: "left", fontSize: 11, fontWeight: 500, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.04em" }}>Fournisseur</th>
                <th style={{ padding: "6px 12px", textAlign: "left", fontSize: 11, fontWeight: 500, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.04em" }}>N° de compte</th>
                <th style={{ padding: "6px 12px", textAlign: "left", fontSize: 11, fontWeight: 500, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.04em" }}>Taux TVA</th>
                <th style={{ padding: "6px 12px", textAlign: "left", fontSize: 11, fontWeight: 500, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.04em" }}>Numéro</th>
                <th style={{ padding: "6px 12px", textAlign: "right", fontSize: 11, fontWeight: 500, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.04em" }}>Montant</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ padding: 48, textAlign: "center", color: "#475569", fontSize: 14 }}>
                    {factures.length === 0 ? "Aucune facture d'achat. Importez des factures depuis Gestion → Factures d'achat." : "Aucune facture ne correspond aux filtres."}
                  </td>
                </tr>
              ) : (
                paginatedFactures.map((f) => {
                  const isSelected = selectedFacture?.id === f.id;
                  const badge = comptaBadgeForStatut(f.statut);
                  const compteLbl = compteChargeListeFactureAchat(
                    f,
                    chargesParFactureId[f.id],
                    f.fournisseur_fk ? compteChargeParFournisseurFk[f.fournisseur_fk] : null
                  );
                  const comptePillStyle = pillCompteCharge(compteLbl);
                  const tvaPill = tauxTvaPillFactureAchat(f);
                  return (
                    <tr
                      key={f.id}
                      onClick={() => {
                        setSelectedFacture(f);
                        setDrawerTab("ecritures");
                      }}
                      style={{
                        cursor: "pointer",
                        background: isSelected ? "linear-gradient(90deg, rgba(255,149,0,0.08), rgba(255,149,0,0.04))" : "transparent",
                        borderBottom: "1px solid #f3f4f6",
                        borderLeft: isSelected ? "4px solid #FF9500" : "4px solid transparent",
                        transition: "background 0.15s ease",
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected) e.currentTarget.style.background = "#f9fafb";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = isSelected
                          ? "linear-gradient(90deg, rgba(255,149,0,0.08), rgba(255,149,0,0.04))"
                          : "transparent";
                      }}
                    >
                      <td style={{ padding: "6px 10px", textAlign: "center", verticalAlign: "middle" }}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(f.id)}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => {
                            const newSet = new Set(selectedIds);
                            if (e.target.checked) {
                              newSet.add(f.id);
                            } else {
                              newSet.delete(f.id);
                            }
                            setSelectedIds(newSet);
                          }}
                          style={{ width: 14, height: 14, accentColor: "#f97316", cursor: "pointer", verticalAlign: "middle" }}
                        />
                      </td>
                      <td style={{ padding: "6px 12px", textAlign: "center", verticalAlign: "middle" }}>
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            fontSize: 11,
                            fontWeight: 600,
                            padding: "2px 8px",
                            borderRadius: 999,
                            background: badge.bg,
                            color: badge.color,
                            border: `1px solid ${badge.border}`,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {badge.label}
                        </span>
                      </td>
                      <td style={{ padding: "6px 12px", fontSize: 12, color: "#64748b", verticalAlign: "middle" }}>{fmtDate(f.date_emission)}</td>
                      <td style={{ padding: "6px 12px", fontSize: 14, fontWeight: 500, color: "#111827", verticalAlign: "middle" }}>{f.fournisseur_nom || "—"}</td>
                      <td
                        style={{ padding: "6px 12px", verticalAlign: "middle" }}
                        title="Compte de charge : écriture comptable si générée, sinon fiche fournisseur, sinon règles métier (658 par défaut)."
                      >
                        <ListeFacturePill text={compteLbl} style={comptePillStyle} mono />
                      </td>
                      <td style={{ padding: "6px 12px", verticalAlign: "middle" }}>
                        <ListeFacturePill text={tvaPill.label} style={tvaPill.style} />
                      </td>
                      <td style={{ padding: "6px 12px", fontSize: 12, color: "#9ca3af", verticalAlign: "middle" }}>{f.numero || "—"}</td>
                      <td style={{ padding: "6px 12px", textAlign: "right", fontSize: 14, fontWeight: 500, color: "#dc2626", fontVariantNumeric: "tabular-nums", verticalAlign: "middle" }}>
                        −{fmt(Math.abs(Number(f.montant_ttc) || 0))}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        )}
      </div>

      {!loading && totalCount > 0 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 24px",
            borderTop: "1px solid #e2e8f0",
            background: "white",
            flexShrink: 0,
          }}
        >
          <span style={{ fontSize: 13, color: "#64748b" }}>
            {startIdx}–{endIdx} sur {totalCount} facture{totalCount > 1 ? "s" : ""}
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={safePage <= 1}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 14px",
                borderRadius: 8,
                border: "1px solid #e2e8f0",
                background: safePage <= 1 ? "#f8fafc" : "white",
                color: safePage <= 1 ? "#94a3b8" : "#475569",
                fontSize: 13,
                fontWeight: 600,
                cursor: safePage <= 1 ? "not-allowed" : "pointer",
                fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
              }}
            >
              <ChevronLeft size={18} strokeWidth={2} />
              Précédent
            </button>
            <span style={{ fontSize: 13, fontWeight: 600, color: "#0f172a", minWidth: 80, textAlign: "center" }}>
              Page {safePage} / {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={safePage >= totalPages}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 14px",
                borderRadius: 8,
                border: "1px solid #e2e8f0",
                background: safePage >= totalPages ? "#f8fafc" : "white",
                color: safePage >= totalPages ? "#94a3b8" : "#475569",
                fontSize: 13,
                fontWeight: 600,
                cursor: safePage >= totalPages ? "not-allowed" : "pointer",
                fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
              }}
            >
              Suivant
              <ChevronRight size={18} strokeWidth={2} />
            </button>
          </div>
        </div>
      )}

      {selectedIds.size > 0 && (
        <div className="sticky bottom-4 z-10 flex justify-center">
          <button
            type="button"
            onClick={() => void handleValidateSelected()}
            disabled={validating}
            className="flex items-center gap-2 rounded-lg bg-green-600 px-6 py-3 font-medium text-white shadow-sm transition-all hover:bg-green-700 disabled:opacity-50"
          >
            <CheckCircle className="h-5 w-5" />
            {validating
              ? "Validation en cours..."
              : `Valider ${selectedIds.size} écriture${selectedIds.size > 1 ? "s" : ""}`}
          </button>
        </div>
      )}

      {showSuccessModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="animate-in fade-in zoom-in mx-4 w-full max-w-md overflow-hidden rounded-lg bg-white shadow-sm duration-200">
            <div className="bg-gradient-to-r from-green-500 to-green-600 px-6 py-8 text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-white/20">
                <CheckCircle className="h-10 w-10 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white">Écritures validées</h2>
            </div>

            <div className="px-6 py-6">
              <p className="mb-4 text-center text-slate-700">
                <span className="font-semibold text-green-600">
                  {validatedCount} écriture{validatedCount > 1 ? "s" : ""}
                </span>{" "}
                {validatedCount > 1 ? "ont été validées" : "a été validée"} avec succès.
              </p>

              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                <div className="flex gap-3">
                  <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500" />
                  <div className="text-sm text-amber-800">
                    <p className="mb-1 font-medium">Important</p>
                    <p>
                      Les écritures validées ne peuvent plus être modifiées directement. En cas d&apos;erreur, vous devrez{" "}
                      <strong>contre-passer l&apos;écriture</strong> et en créer une nouvelle.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="px-6 pb-6">
              <button
                type="button"
                onClick={() => setShowSuccessModal(false)}
                className="w-full rounded-lg bg-slate-900 py-3 font-medium text-white transition-colors hover:bg-slate-800"
              >
                Compris
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedFacture && (
        <ComptaDrawer open={!!selectedFacture} onClose={() => setSelectedFacture(null)} width={560}>
          <div className="flex flex-1 flex-col overflow-hidden">
            <div className="flex-shrink-0 border-b border-[#e4e8ef] bg-white px-5 py-4">
              <div className="mb-2 flex justify-end">
                <Button type="button" variant="ghost" size="sm" className="!p-1.5" onClick={() => setSelectedFacture(null)} aria-label="Fermer">
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="mb-1 text-2xl font-bold tabular-nums text-red-600">− {fmt(Math.abs(Number(selectedFacture.montant_ttc) || 0))}</div>
              <div className="flex flex-wrap items-center gap-2">
                <div className="text-[15px] font-semibold text-slate-900">{selectedFacture.fournisseur_nom || "—"}</div>
                <div className="text-[12px] text-slate-400">{fmtDate(selectedFacture.date_emission)}</div>
                {badgeMetier && (
                  <span
                    className="rounded-full px-2 py-0.5 text-[11px] font-semibold"
                    style={{ background: badgeMetier.bg, color: badgeMetier.color, border: `1px solid ${badgeMetier.border}` }}
                  >
                    {badgeMetier.label}
                  </span>
                )}
                <span
                  className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold"
                  style={{
                    background: statutComptable === "Validé" ? "#f0fdf4" : statutComptable === "Non généré" ? "#f8fafc" : "#fff7ed",
                    color: statutComptable === "Validé" ? "#15803d" : statutComptable === "Non généré" ? "#64748b" : "#c2410c",
                    border:
                      statutComptable === "Validé" ? "1px solid #bbf7d0" : statutComptable === "Non généré" ? "1px solid #e2e8f0" : "1px solid #fed7aa",
                  }}
                >
                  <FileCheck size={12} strokeWidth={2} className="shrink-0" />
                  {statutComptable}
                </span>
              </div>
            </div>

            <div className={cn(formTabsListClass, "!mb-0 flex-shrink-0 bg-white px-2")}>
              {(
                [
                  { id: "ecritures" as const, label: "Écritures", icon: BookOpen },
                  { id: "reconciliation" as const, label: "Réconciliation", icon: ArrowLeftRight },
                  { id: "commentaires" as const, label: "Commentaires", icon: MessageCircle },
                ] as const
              ).map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setDrawerTab(t.id)}
                  className={cn(
                    formTabButtonClass,
                    drawerTab === t.id && formTabButtonActiveClass,
                    "inline-flex items-center gap-1.5"
                  )}
                >
                  <t.icon size={14} className="shrink-0" />
                  {t.label}
                  {t.id === "commentaires" && commentairesCount > 0 && (
                    <span className="ml-1 inline-flex min-h-4 min-w-[15px] items-center justify-center rounded-full bg-amber-50 px-1.5 py-px text-[10px] font-semibold text-amber-700">
                      {commentairesCount}
                    </span>
                  )}
                </button>
              ))}
            </div>

            <div className="flex flex-1 flex-col gap-4 overflow-y-auto bg-white px-5 py-4">
              {drawerTab === "ecritures" && (
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  <section>
                    <h3 style={{ fontSize: "0.8rem", fontWeight: 800, color: "#0f172a", marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
                      <Receipt size={16} strokeWidth={2} color="#F97316" />
                      Facture
                    </h3>
                    {ecritureFacture === "loading" ? (
                      <div style={{ padding: 32, textAlign: "center", color: "#64748b" }}>
                        <Loader2 size={24} style={{ animation: "spin 1s linear infinite" }} />
                      </div>
                    ) : (
                      <BlocEcriture
                        titre="Écriture d'achat (D6/D4452/C4011)"
                        ecriture={ecritureFacture}
                        emptyMessage="Aucune écriture comptable générée"
                        emptySubtext="L'écriture sera créée lors de la validation métier de la facture ou lors du passage en comptabilité."
                        placeholderRows={[
                          { c: "6xx", l: "Charge" },
                          { c: "4452", l: "TVA déductible" },
                          { c: "4011", l: "Fournisseurs" },
                        ]}
                        compactTable
                        onEditDraft={
                          ecritureFacture?.statut === "draft" ? () => setEditingEcriture(ecritureFacture) : undefined
                        }
                      />
                    )}
                  </section>

                  {ecritureFacture !== "loading" && ecritureRetentionTva && (
                    <section>
                      <h3 style={{ fontSize: "0.8rem", fontWeight: 800, color: "#0f172a", marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
                        <Receipt size={16} strokeWidth={2} color="#6366f1" />
                        Rétention TVA (encaissements)
                      </h3>
                      <BlocEcriture
                        titre="Neutralisation TVA jusqu'au paiement (D4454/C4452)"
                        ecriture={ecritureRetentionTva}
                        emptyMessage="—"
                        emptySubtext=""
                        placeholderRows={[
                          { c: "4454", l: "TVA en attente" },
                          { c: "4452", l: "TVA récupérable sur achats" },
                        ]}
                        compactTable
                        onEditDraft={
                          ecritureRetentionTva.statut === "draft" ? () => setEditingEcriture(ecritureRetentionTva) : undefined
                        }
                      />
                    </section>
                  )}

                  {selectedFacture.cca_detecte && (
                    <section>
                      <h3 style={{ fontSize: "0.8rem", fontWeight: 800, color: "#0f172a", marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
                        <FileCheck size={16} strokeWidth={2} color="#0d9488" />
                        Charges constatées d&apos;avance (CCA)
                      </h3>
                      {ecritureCCA === "loading" ? (
                        <div style={{ padding: 24, textAlign: "center", color: "#64748b" }}>
                          <Loader2 size={22} style={{ animation: "spin 1s linear infinite" }} />
                        </div>
                      ) : ecritureCCA ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                          <BlocEcriture
                            titre="Écriture CCA (D476 / C charge, journal OD)"
                            ecriture={ecritureCCA}
                            emptyMessage="—"
                            emptySubtext=""
                            placeholderRows={[
                              { c: "476", l: "Charges constatées d'avance" },
                              { c: "6xx", l: "Charge (crédit)" },
                            ]}
                            compactTable
                            onEditDraft={ecritureCCA.statut === "draft" ? () => setEditingEcriture(ecritureCCA) : undefined}
                          />
                          {ecritureCCA.extournable && ecritureCCA.date_extourne && (
                            <p style={{ margin: 0, fontSize: 12, color: "#64748b" }}>
                              Extourne prévue le {fmtDate(ecritureCCA.date_extourne)}.
                            </p>
                          )}
                        </div>
                      ) : !selectedFacture.cca_genere ? (
                        (() => {
                          const montantCca = Math.round(Number(selectedFacture.cca_montant) || 0);
                          const pr = selectedFacture.cca_prorata;
                          const prPct =
                            pr != null && Number.isFinite(Number(pr)) ? Math.round(Number(pr) * 100) : null;
                          const dateOd = exercicePourFacture?.date_fin;
                          const chargePrev = premiereLigneChargeHt(ecritureFacture);
                          const libelleCca = `CCA - ${(selectedFacture.numero || "facture").trim()}`;
                          return (
                            <div
                              style={{
                                background: "#f0fdfa",
                                borderRadius: 10,
                                padding: "14px 16px",
                                border: "1px solid #99f6e4",
                              }}
                            >
                              <p style={{ margin: "0 0 10px 0", fontSize: 13, color: "#0f766e", fontWeight: 600 }}>CCA à générer</p>
                              <div style={{ fontSize: 13, color: "#334155", lineHeight: 1.5, marginBottom: 12 }}>
                                {prPct != null ? (
                                  <div>
                                    Prorata (part sur l&apos;exercice suivant) : <strong>{prPct} %</strong>
                                  </div>
                                ) : (
                                  <div style={{ color: "#64748b" }}>Prorata : non renseigné</div>
                                )}
                                <div>
                                  Montant CCA : <strong>{fmt(montantCca)}</strong>
                                </div>
                                {!exercicePourFacture && (
                                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8, color: "#b45309" }}>
                                    <AlertTriangle size={16} />
                                    Aucun exercice ne contient la date d&apos;émission : la date OD ne peut pas être déterminée ici.
                                  </div>
                                )}
                              </div>
                              <div
                                style={{
                                  fontSize: 12,
                                  fontFamily: "'JetBrains Mono', 'Space Mono', monospace",
                                  background: "white",
                                  borderRadius: 8,
                                  padding: "10px 12px",
                                  border: "1px solid #ccfbf1",
                                  color: "#134e4a",
                                  marginBottom: 14,
                                }}
                              >
                                <div>Journal OD · {dateOd ? fmtDate(dateOd) : "—"}</div>
                                <div style={{ marginTop: 6 }}>Libellé : {libelleCca}</div>
                                <div style={{ marginTop: 8 }}>
                                  D 47600000 Charges constatées d&apos;avance · {fmt(montantCca)}
                                </div>
                                <div>
                                  C {chargePrev ? `${chargePrev.compte} ${chargePrev.libelle}` : "6xx (charge HT facture)"} · {fmt(montantCca)}
                                </div>
                              </div>
                              <button
                                type="button"
                                disabled={
                                  generatingCca || !exercicePourFacture || montantCca <= 0 || ecritureFacture === "loading"
                                }
                                onClick={() => void handleGenererEcritureCca()}
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 8,
                                  padding: "10px 18px",
                                  borderRadius: 8,
                                  border: "none",
                                  background:
                                    generatingCca || !exercicePourFacture || montantCca <= 0 ? "#94a3b8" : "#0d9488",
                                  color: "white",
                                  fontSize: 13,
                                  fontWeight: 700,
                                  cursor:
                                    generatingCca || !exercicePourFacture || montantCca <= 0 ? "not-allowed" : "pointer",
                                  fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
                                }}
                              >
                                {generatingCca ? (
                                  <Loader2 size={18} className="animate-spin" style={{ animation: "spin 1s linear infinite" }} />
                                ) : (
                                  <FileCheck size={18} strokeWidth={2} />
                                )}
                                Générer l&apos;écriture CCA
                              </button>
                            </div>
                          );
                        })()
                      ) : (
                        <p style={{ margin: 0, fontSize: 13, color: "#64748b" }}>
                          Écriture CCA marquée comme générée ; si elle n&apos;apparaît pas, rafraîchissez la page.
                        </p>
                      )}
                    </section>
                  )}

                  <section>
                    <h3 style={{ fontSize: "0.8rem", fontWeight: 800, color: "#0f172a", marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
                      <BookOpen size={16} strokeWidth={2} color="#6366f1" />
                      Résumé comptable
                    </h3>
                    <div style={{ background: "#FAFAFA", borderRadius: 10, padding: "14px 16px", border: "1px solid #E2E8F0" }}>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
                        {configType && (
                          <span style={{ padding: "6px 12px", borderRadius: 8, fontSize: "0.8rem", fontWeight: 600, background: "#EEF2FF", color: "#4338ca", border: "1px solid #C7D2FE" }}>
                            {configType.label}
                          </span>
                        )}
                        {regimeTvaTypeDepense && (
                          <span style={{ padding: "6px 12px", borderRadius: 8, fontSize: "0.8rem", fontWeight: 600, background: "#E0F2FE", color: "#0369a1", border: "1px solid #BAE6FD" }}>
                            {getLabelRegimeTva(regimeTvaTypeDepense as never)}
                          </span>
                        )}
                        <span
                          style={{
                            padding: "6px 12px",
                            borderRadius: 8,
                            fontSize: "0.8rem",
                            fontWeight: 600,
                            background: "#f1f5f9",
                            color: "#334155",
                            border: "1px solid #e2e8f0",
                          }}
                          title="Exigibilité TVA déductible (facture)"
                        >
                          {labelExigibiliteTvaFactureAchat(exigibiliteTvaFacture)}
                        </span>
                        {ecritureFacture && ecritureFacture !== "loading" && (
                          <span
                            style={{
                              padding: "6px 12px",
                              borderRadius: 8,
                              fontSize: "0.78rem",
                              fontWeight: 600,
                              fontFamily: "'JetBrains Mono', 'Space Mono', monospace",
                              background: "#FFF7ED",
                              color: "#9a3412",
                              border: "1px solid #FED7AA",
                            }}
                          >
                            {ecritureFacture.journal_code} · Facture achat
                          </span>
                        )}
                      </div>
                    </div>
                  </section>
                </div>
              )}

              {drawerTab === "reconciliation" && (
                <div style={{ padding: 8, fontSize: 14, color: "#475569", lineHeight: 1.6 }}>
                  <p style={{ margin: "0 0 12px 0" }}>
                    Rapprochez les paiements bancaires avec cette facture depuis <strong>Gestion → Factures d&apos;achat</strong>.
                  </p>
                  <p style={{ margin: "0 0 16px 0", color: "#64748b", fontSize: 13 }}>
                    Statut rapprochement :{" "}
                    <strong>{selectedFacture.statut_rapprochement === "rapprochee" ? "Rapprochée" : selectedFacture.statut_rapprochement === "partiellement_rapprochee" ? "Partielle" : "Non rapprochée"}</strong>
                  </p>
                  <div style={{ borderTop: "1px solid #e2e8f0", paddingTop: 16 }}>
                    <h4 style={{ fontSize: 12, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", margin: "0 0 12px 0" }}>
                      Pièce justificative de la facture
                    </h4>
                    <input
                      ref={facturePieceInputRef}
                      type="file"
                      accept=".pdf,application/pdf,image/*"
                      style={{ display: "none" }}
                      onChange={handleFacturePieceFileSelected}
                    />
                    {(() => {
                      const stored = selectedFacture.file_url?.trim() ?? "";
                      const url = (resolvedFactureFileUrl ?? stored).trim();
                      if (stored) {
                        const displayName = displayFileNameFromUrl(stored);
                        const remplacerBtn = (
                          <button
                            type="button"
                            disabled={uploadingFacturePiece}
                            onClick={() => facturePieceInputRef.current?.click()}
                            style={{
                              flexShrink: 0,
                              alignSelf: "flex-start",
                              padding: "8px 14px",
                              borderRadius: 8,
                              border: "1px solid #e2e8f0",
                              background: uploadingFacturePiece ? "#f1f5f9" : "white",
                              color: "#475569",
                              fontSize: 12,
                              fontWeight: 600,
                              cursor: uploadingFacturePiece ? "not-allowed" : "pointer",
                              fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
                            }}
                          >
                            Remplacer
                          </button>
                        );
                        if (pieceUrlIsLikelyPdf(stored)) {
                          return (
                            <div
                              style={{
                                height: 120,
                                boxSizing: "border-box",
                                background: "#f8fafc",
                                border: "1px solid #e2e8f0",
                                borderRadius: 10,
                                padding: "10px 12px",
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "space-between",
                                gap: 8,
                              }}
                            >
                              <div style={{ display: "flex", alignItems: "flex-start", gap: 10, minHeight: 0 }}>
                                <FileText size={26} strokeWidth={2} color="#dc2626" style={{ flexShrink: 0, marginTop: 2 }} />
                                <span
                                  style={{
                                    fontSize: 13,
                                    fontWeight: 700,
                                    color: "#0f172a",
                                    lineHeight: 1.35,
                                    overflow: "hidden",
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    wordBreak: "break-word",
                                  }}
                                >
                                  {displayName}
                                </span>
                              </div>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                                <a
                                  href={url || "#"}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    padding: "8px 14px",
                                    borderRadius: 8,
                                    border: "none",
                                    background: "#FF9500",
                                    color: "white",
                                    fontSize: 12,
                                    fontWeight: 600,
                                    textDecoration: "none",
                                    fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
                                  }}
                                >
                                  Ouvrir le PDF
                                </a>
                                <button
                                  type="button"
                                  disabled={uploadingFacturePiece}
                                  onClick={() => facturePieceInputRef.current?.click()}
                                  style={{
                                    marginLeft: "auto",
                                    padding: "8px 14px",
                                    borderRadius: 8,
                                    border: "1px solid #e2e8f0",
                                    background: uploadingFacturePiece ? "#f1f5f9" : "white",
                                    color: "#475569",
                                    fontSize: 12,
                                    fontWeight: 600,
                                    cursor: uploadingFacturePiece ? "not-allowed" : "pointer",
                                    fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
                                  }}
                                >
                                  Remplacer
                                </button>
                              </div>
                            </div>
                          );
                        }
                        return (
                          <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: 12, background: "#fafafa" }}>
                            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap: 12 }}>
                              <div style={{ flex: "1 1 200px", minWidth: 0 }}>
                                {pieceUrlIsLikelyImage(stored) ? (
                                  <a href={url || "#"} target="_blank" rel="noopener noreferrer" style={{ display: "block", lineHeight: 0 }}>
                                    <img
                                      src={url || ""}
                                      alt={displayName}
                                      style={{ maxHeight: 150, maxWidth: "100%", borderRadius: 8, objectFit: "contain", cursor: "pointer" }}
                                    />
                                  </a>
                                ) : (
                                  <a
                                    href={url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 600, color: "#0f172a" }}
                                  >
                                    <FileIcon size={24} strokeWidth={1.75} color="#64748b" style={{ flexShrink: 0 }} />
                                    <span style={{ wordBreak: "break-word" }}>{displayName}</span>
                                  </a>
                                )}
                              </div>
                              {remplacerBtn}
                            </div>
                          </div>
                        );
                      }
                      return (
                        <button
                          type="button"
                          disabled={uploadingFacturePiece}
                          onClick={() => facturePieceInputRef.current?.click()}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 8,
                            padding: "10px 16px",
                            borderRadius: 10,
                            border: "1px solid #e2e8f0",
                            background: uploadingFacturePiece ? "#f1f5f9" : "white",
                            color: "#0f172a",
                            fontSize: 13,
                            fontWeight: 600,
                            cursor: uploadingFacturePiece ? "not-allowed" : "pointer",
                            fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
                          }}
                        >
                          {uploadingFacturePiece ? (
                            <Loader2 size={18} className="animate-spin" style={{ animation: "spin 1s linear infinite" }} />
                          ) : (
                            <Upload size={18} strokeWidth={2} />
                          )}
                          Importer une pièce
                        </button>
                      );
                    })()}
                  </div>
                </div>
              )}

              {drawerTab === "commentaires" && activeDossier?.id && (
                <div style={{ minHeight: 420, maxHeight: "70vh", display: "flex", flexDirection: "column" }}>
                  <CommentairesTab
                    dossierId={activeDossier.id}
                    entiteType="facture_achat"
                    entiteId={selectedFacture.id}
                    onCommentsChanged={() => {
                      void countCommentaires("facture_achat", selectedFacture.id).then(setCommentairesCount);
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </ComptaDrawer>
      )}

      {editingEcriture && (
        <EcritureEditModal
          ecriture={editingEcriture}
          onClose={() => setEditingEcriture(null)}
          onSaved={() => setEcrituresRefreshKey((k) => k + 1)}
          onValidated={() => setEcrituresRefreshKey((k) => k + 1)}
        />
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
